import { useEffect, useRef, useState } from "react";
import { ChevronDown, Download, FileText, Loader2 } from "lucide-react";
import { useExportProposal } from "@/hooks/use-proposals";

interface ExportMenuProps {
  proposalId: string;
}

/**
 * Menú de exportación de una propuesta a DOCX o PDF.
 *
 * Usa `useExportProposal` que se encarga de descargar el blob y mostrar toasts.
 */
export function ExportMenu({ proposalId }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const exportMutation = useExportProposal(proposalId);

  // Cerrar el dropdown al hacer click fuera
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleExport = (format: "docx" | "pdf") => {
    setIsOpen(false);
    exportMutation.mutate({ format });
  };

  const isExporting = exportMutation.isPending;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isExporting}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {isExporting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Exportando...</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>Exportar</span>
            <ChevronDown className="w-4 h-4" />
          </>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => handleExport("docx")}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <FileText className="w-4 h-4" />
            <span>Exportar DOCX</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => handleExport("pdf")}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <FileText className="w-4 h-4" />
            <span>Exportar PDF</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportMenu;
